import { Component, computed, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NotificationGridItemObject } from '@it2go/types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DividerModule } from '@openng/optimus-ui/divider';
import { LayoutService } from '../../services/layout.service';
import { FilterBuilder } from '../../../content/helper/filter.builder';
import { NotificationSeverity } from '@libs/types/src/notifications/enum/notification-severity.enum';
import { BackgroundTaskStatus } from '@libs/types/src/notifications/enum/background-task.enum';
import {
    fb,
    isAllowed,
    translate,
} from '../../../shared/shared.global-service';
import { NotificationsStore } from './notifications.store';
import { InfiniteScrollStore } from '../../../shared/store/common/infinite-scroll.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppFormGroup } from '../../../../utils/form.utils';
import { SelectButtonComponent } from '../../../../components/kit/select-button/select-button.component';
import { SelectButtonOption } from '../../../../components/kit/select-button/select-button.types';
import { InfiniteScrollLoadOnScreenComponent } from '../../../../components/kit/infinite-scroll/infinite-scroll-load-on-screen/infinite-scroll-load-on-screen.component';
import { DropDownMenuComponent } from '../../../../components/kit/menu/drop-down-menu/drop-down-menu.component';
import { ButtonComponent } from '../../../../components/kit/button/button.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { Tag } from '@openng/optimus-ui/tag';
import { MenuItem, MenuItemCommandEvent } from '@openng/optimus-ui/api';
import { successDeleted } from '../../../content/content.error';
import { ConfirmBuilder } from '../../../shared/service/confirm.builder';
import { EmptyState } from '../../../shared/components/empty-state/empty-state.types';
import { ParseParamsPipe } from './notifications.pipes';
import { LocalDatePipe } from '../../../shared/pipes/local-date.pipe';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { BackgroundTaskStore } from '../../store/background-tasks/background-task.store';
import { BackgroundTaskItemComponent } from '../background-task-item/background-task-item.component';
import { NotificationFeedItem } from './notifications.types';

const ACTIVE_BACKGROUND_TASK_STATUSES = new Set<BackgroundTaskStatus>([
    BackgroundTaskStatus.QUEUED,
    BackgroundTaskStatus.RUNNING,
]);

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    imports: [
        TranslatePipe,
        RouterLink,
        AccordionModule,
        AvatarModule,
        DividerModule,
        ButtonModule,
        SelectButtonComponent,
        InfiniteScrollLoadOnScreenComponent,
        DropDownMenuComponent,
        ButtonComponent,
        Tooltip,
        Tag,
        ParseParamsPipe,
        LocalDatePipe,
        EmptyStateComponent,
        BackgroundTaskItemComponent,
    ],
    providers: [NotificationsStore],
})
export class NotificationsComponent {
    public readonly router = inject(Router);
    public readonly layoutService = inject(LayoutService);
    public readonly store = inject(NotificationsStore);
    public readonly translate = inject(TranslateService);
    public readonly confirm = inject(ConfirmBuilder);
    protected readonly backgroundTaskStore = inject(BackgroundTaskStore);

    protected unreadOptions = [
        <SelectButtonOption>{ value: '1', key: 'unread' },
        <SelectButtonOption>{ value: '0', key: 'all' },
    ];

    protected emptyState = new EmptyState(
        'pi pi-bell-slash',
        'layout.notifications.emptyState.title',
        'layout.notifications.emptyState.text',
    );

    protected actionsMenu = computed<MenuItem[]>(() => {
        const unreadValue = this.filterChange();

        const actions: MenuItem[] = [
            {
                label: translate.instant(
                    'layout.notifications.actions.readAll',
                ),
                command: () => void this.readAll(true),
            },
        ];

        if (unreadValue?.unread === '0') {
            actions.push(
                {
                    label: translate.instant(
                        'layout.notifications.actions.unreadAll',
                    ),
                    command: () => void this.readAll(false),
                },
                {
                    label: translate.instant(
                        'layout.notifications.actions.deleteAll',
                    ),
                    command: (event: MenuItemCommandEvent) =>
                        this.deleteAll(event),
                },
            );
        }

        return actions;
    });

    protected notificationsScroll = computed(() => {
        this.filterChange();
        const builder = new FilterBuilder();
        this.createFilters(builder);

        return new InfiniteScrollStore<NotificationGridItemObject>(
            this.store.api.getNotifications,
            builder.filter(),
            true,
        );
    });

    protected filteringForm = new AppFormGroup({
        unread: fb.control<string>('1'),
    });

    protected filterChange = toSignal(this.filteringForm.valueChanges);

    protected readonly activeTasks = computed(() =>
        this.backgroundTaskStore
            .tasks()
            .filter((task) => ACTIVE_BACKGROUND_TASK_STATUSES.has(task.status)),
    );

    protected readonly feedItems = computed<NotificationFeedItem[]>(() => {
        const showUnread = this.filterChange()?.unread !== '0';
        const notifications: NotificationFeedItem[] = this.notificationsScroll()
            .items()
            .map((notification) => ({
                kind: 'notification',
                id: notification.id,
                occurredAt: notification.createdAt,
                notification,
            }));
        const tasks: NotificationFeedItem[] = this.backgroundTaskStore
            .tasks()
            .filter(
                (task) =>
                    !ACTIVE_BACKGROUND_TASK_STATUSES.has(task.status) &&
                    (!showUnread || !task.isRead),
            )
            .map((task) => ({
                kind: 'backgroundTask',
                id: task.id,
                occurredAt: task.updatedAt,
                task,
            }));

        return [...notifications, ...tasks].sort(
            (a, b) =>
                new Date(b.occurredAt).getTime() -
                new Date(a.occurredAt).getTime(),
        );
    });

    protected readonly hasItems = computed(
        () => this.activeTasks().length > 0 || this.feedItems().length > 0,
    );

    protected handleReadStatus(
        notif: NotificationGridItemObject,
        event: MouseEvent,
    ) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        this.store.api
            .readNotification({
                read: !notif.isRead,
                id: notif.id,
            })
            .then(() => this.notificationsScroll().refresh());
    }

    protected handleRemove(
        notif: NotificationGridItemObject,
        event: MouseEvent,
    ) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteNotification({ id: notif.id });
                successDeleted();
                this.notificationsScroll().refresh();
            },
            { event },
        );
    }

    protected async readAll(type: boolean): Promise<void> {
        await Promise.all([
            this.store.api.readAllNotifications({ read: type }),
            type ? this.backgroundTaskStore.markAllRead() : Promise.resolve(),
        ]);
        await this.notificationsScroll().refresh();
    }

    protected deleteAll(event: MenuItemCommandEvent) {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteAllNotifications();
                successDeleted();
                this.notificationsScroll().refresh();
            },
            { event: event.originalEvent! },
        );
    }

    private createFilters(builder: FilterBuilder): void {
        const showUnread: boolean = this.filteringForm.value.unread === '1';
        builder.where(
            'userId',
            this.store.globalGlobalStore.loggedUserStore.currentUser()?.id,
        );
        showUnread && builder.where('isRead', false);
        builder.sort('createdAt', 'DESC');
    }

    protected readonly NotificationSeverity = NotificationSeverity;
    protected readonly notificationTagSeverity: Record<
        NotificationSeverity,
        'info' | 'success' | 'warn' | 'danger'
    > = {
        [NotificationSeverity.INFO]: 'info',
        [NotificationSeverity.SUCCESS]: 'success',
        [NotificationSeverity.WARNING]: 'warn',
        [NotificationSeverity.CRITICAL]: 'danger',
    };
    protected readonly notificationTagIcon: Record<
        NotificationSeverity,
        string
    > = {
        [NotificationSeverity.INFO]: 'pi-info-circle',
        [NotificationSeverity.SUCCESS]: 'pi-check',
        [NotificationSeverity.WARNING]: 'pi-exclamation-triangle',
        [NotificationSeverity.CRITICAL]: 'pi-times',
    };
    protected readonly notificationTagLabel: Record<
        NotificationSeverity,
        string
    > = {
        [NotificationSeverity.INFO]: 'info',
        [NotificationSeverity.SUCCESS]: 'success',
        [NotificationSeverity.WARNING]: 'warn',
        [NotificationSeverity.CRITICAL]: 'error',
    };
    protected readonly isAllowed = isAllowed;
}

export default NotificationsComponent;
